/**
 * Gallery axis strategy for 'step' mode.
 *
 * Displays one row per step, with run and index controlled via sliders/dropdowns.
 */

import {sampleSorted} from '@wandb/weave/common/util/data';

import {type GridRow, type SlotRegistry, type TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {caption, compareEntry, indexSlider, label} from '../utils/slots';
import {createEmptySlotRegistry} from '../utils/slotUtils';
import {type GalleryAxisStrategy} from './GalleryAxisStrategy';

export class StepAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {stepMeta} = input;

    // Use sliderSteps (sampled) if available, otherwise use all steps
    let steps = stepMeta.sliderSteps ?? stepMeta.steps;

    // Sample steps if we exceed the gallery item limit
    if (maxItems && steps.length > maxItems) {
      steps = sampleSorted(steps, maxItems);
    }

    return steps;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, config, allMasks, mediaType} = input;
    const {mediaKeys} = config;
    const currentIndex = config.mediaIndex ?? 0;

    if (!mediaKeys || mediaKeys.length === 0 || !mediaType) {
      return [];
    }

    // Use first run for step axis
    const run = runs[0];
    if (!run) {
      return [];
    }

    const runStepMeta = stepMeta.runs.find(r => r.id === run.name);
    if (!runStepMeta) {
      return [];
    }
    const rows: GridRow[] = [];

    for (const stepValue of axisValues) {
      const cells = createCellsForMediaKeys(
        run,
        runStepMeta,
        stepValue,
        currentIndex,
        mediaKeys,
        config,
        allMasks,
        mediaType
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `step-${stepValue}`,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(
    stepMeta?: TransformInput['stepMeta'],
    isSingleRunView?: boolean
  ): SlotRegistry {
    // Step axis: steps shown on each tile, run and index are controlled
    const slots = createEmptySlotRegistry();
    slots.tileHeader = [label('step')];
    slots.tileFooter = [caption()];
    slots.contentHeader = [
      {
        control: 'pagination',
        type: 'component',
      },
    ];

    // Only add index slider if there are multiple indexes
    // Index slider defaults to center when alone (handled by ContentFooterSlot)
    const maxIndex = stepMeta?.maxIndex ?? 0;
    if (maxIndex > 0) {
      slots.contentFooter!.push(indexSlider());
    }

    return slots;
  }
}
